import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Ban,
  Clock,
  KeyRound,
  Laptop,
  Loader2,
  LogOut,
  Mail,
  MoreHorizontal,
  Monitor,
  Pause,
  Pencil,
  Play,
  Plus,
  ScrollText,
  ShieldCheck,
  Smartphone,
  Tablet,
  Trash2,
  UserCheck,
  UserX,
  CheckCircle2,
  Package,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { CopyId, StatusPill } from '@/components/page';
import { EmptyState, MarkTile, Notice, ago, fmtDate, plural, rowLink } from '@/components/workspace-ui';
import { ConfirmDialog, SecretDialog, UserAvatar, fmtDateTime } from '@/components/admin-ui';
import {
  useUser,
  useUpdateUser,
  useDeleteUser,
  useUserProducts,
  useSetUserOrganization,
  useRemoveUserOrganization,
  useOrganizationMembers,
  useProducts,
  useGrantProductAccess,
  useRevokeProductAccess,
  useSetProductAccessActive,
  useUpdateProductAccess,
  useProductRoles,
  useCreateProductRole,
  useOrganizations,
  useUserSessions,
  useRevokeUserSession,
  useResendVerification,
  useForceReset,
  useToast,
  useAuth,
} from '@/hooks';
import { cn } from '@/lib/utils';
import { auditApi, usersApi } from '@/api';
import type { UserAppAccess, UserSessionInfo } from '@/api/users';
import type { OrganizationRole } from '@/types';

type Role = OrganizationRole;
const ORG_ROLES: Role[] = ['owner', 'admin', 'member', 'viewer'];
const TABS = ['overview', 'access', 'sessions', 'activity', 'settings'] as const;
type Tab = (typeof TABS)[number];

const errMsg = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback);

type GrantState = 'active' | 'expiring' | 'expired' | 'suspended';
function grantState(g: UserAppAccess): GrantState {
  if (!g.isActive) return 'suspended';
  if (g.expiresAt) {
    const ms = new Date(g.expiresAt).getTime() - Date.now();
    if (ms <= 0) return 'expired';
    if (ms < 7 * 864e5) return 'expiring';
  }
  return 'active';
}
const GRANT_PILL: Record<GrantState, { tone: 'ok' | 'warn' | 'bad'; label: string }> = {
  active: { tone: 'ok', label: 'Active' },
  expiring: { tone: 'warn', label: 'Expiring' },
  expired: { tone: 'bad', label: 'Expired' },
  suspended: { tone: 'warn', label: 'Suspended' },
};

function tempPassword() {
  return (
    'Tmp-' +
    Array.from(crypto.getRandomValues(new Uint8Array(6)))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
  );
}

type Confirm =
  | { kind: 'disable' | 'enable' | 'delete' | 'forceReset' | 'markVerified' | 'resetPassword' }
  | { kind: 'superAdmin'; on: boolean }
  | { kind: 'revokeSession'; session: UserSessionInfo }
  | { kind: 'revokeAll'; count: number }
  | { kind: 'revokeGrant'; grant: UserAppAccess }
  | { kind: 'toggleGrant'; grant: UserAppAccess; on: boolean }
  | null;

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const isSelf = currentUser?.id === id;

  const { data: user, isLoading, isError, error, refetch } = useUser(id);
  const updateUser = useUpdateUser();
  const deleteUser = useDeleteUser();
  const resend = useResendVerification();
  const forceReset = useForceReset();
  const revokeSessions = useRevokeUserSession();
  const revokeAccess = useRevokeProductAccess();
  const setAccessActive = useSetProductAccessActive();

  const { data: grants = [] } = useUserProducts(id);
  const { data: sessions = [] } = useUserSessions(id);
  const { data: activity } = useQuery({
    queryKey: ['audit', 'user', id],
    queryFn: () => auditApi.listWithTotal({ userId: id, limit: 20 }),
    enabled: !!id,
  });

  const initialTab = (): Tab => {
    const h = window.location.hash.slice(1) as Tab;
    return TABS.includes(h) ? h : 'overview';
  };
  const [tab, setTab] = useState<Tab>(initialTab);
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [grantDialog, setGrantDialog] = useState<{ existing?: UserAppAccess } | null>(null);
  const [secret, setSecret] = useState<string | null>(null);

  useEffect(() => {
    if (user) document.title = `${user.name || user.email} · authrz.me`;
  }, [user]);

  const changeTab = (t: string) => {
    setTab(t as Tab);
    window.history.replaceState(null, '', `#${t}`);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-10 w-80" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError || !user) {
    const notFound = !isError || (error as { status?: number } | null)?.status === 404;
    return (
      <Card>
        {notFound ? (
          <EmptyState
            icon={UserX}
            title="User not found"
            body={
              id ? (
                <>
                  No user has the ID <code className="font-mono">{id}</code>. It may have been
                  deleted.
                </>
              ) : (
                'No user ID was given in the link.'
              )
            }
            action={
              <Button variant="outline" asChild>
                <Link to="/users">
                  <ArrowLeft />
                  Back to users
                </Link>
              </Button>
            }
          />
        ) : (
          <div className="p-4">
            <Notice
              tone="bad"
              title="Couldn’t load this user"
              action={
                <Button size="sm" variant="outline" onClick={() => refetch()}>
                  Retry
                </Button>
              }
            >
              {errMsg(error, 'The request failed.')}
            </Notice>
          </div>
        )}
      </Card>
    );
  }

  const display = user.name || user.email;
  const others = sessions.filter((s) => !s.isCurrent);

  const run = async (fn: () => Promise<unknown>, ok: { title: string; description?: string }) => {
    try {
      await fn();
      toast(ok);
    } catch (e) {
      toast({ variant: 'destructive', title: 'Action failed', description: errMsg(e, 'Try again.') });
    }
  };

  const handleResend = () =>
    run(() => resend.mutateAsync(user.id), {
      title: 'Verification email sent',
      description: user.email,
    });

  const confirmCopy = (() => {
    if (!confirm) return null;
    switch (confirm.kind) {
      case 'disable':
        return {
          title: `Disable ${display}?`,
          body: (
            <>
              <span className="font-mono">{user.email}</span> is signed out of every session and
              can’t sign in until re-enabled. Grants are kept.
            </>
          ),
          confirmLabel: 'Disable account',
          danger: true,
        };
      case 'enable':
        return {
          title: `Enable ${display}?`,
          body: (
            <>
              <span className="font-mono">{user.email}</span> will be able to sign in again.
            </>
          ),
          confirmLabel: 'Enable account',
        };
      case 'forceReset':
        return {
          title: 'Force a password reset?',
          body: (
            <>
              {display} is signed out of every device and must set a new password from the link
              sent to <span className="font-mono">{user.email}</span>.
            </>
          ),
          confirmLabel: 'Send reset link',
          danger: true,
        };
      case 'resetPassword':
        return {
          title: `Set a temporary password for ${display}?`,
          body: 'A new temporary password replaces their current one and is shown once. Share it securely; they should change it after signing in.',
          confirmLabel: 'Set temporary password',
          danger: true,
        };
      case 'markVerified':
        return {
          title: 'Mark email as verified?',
          body: (
            <>
              Use this only if you’ve confirmed <span className="font-mono">{user.email}</span>{' '}
              belongs to {display} another way.
            </>
          ),
          confirmLabel: 'Mark verified',
        };
      case 'delete':
        return {
          title: `Delete ${display}?`,
          body: 'This permanently removes the account, every app grant and their organization membership. Audit events are kept. This can’t be undone.',
          confirmLabel: 'Delete user',
          danger: true,
          typeToConfirm: user.email,
        };
      case 'superAdmin':
        return confirm.on
          ? {
              title: `Make ${display} a super admin?`,
              body: 'Super admins can change any organization, app, OAuth client, signing key and hosted MCP key, read the full audit log, and disable other users. Grant this only to people who operate authrz.me itself.',
              confirmLabel: 'Make super admin',
              danger: true,
            }
          : {
              title: `Remove super admin from ${display}?`,
              body: 'They keep their organization role and app grants but lose every admin page immediately.',
              confirmLabel: 'Remove super admin',
            };
      case 'revokeSession': {
        const s = confirm.session;
        return {
          title: 'Revoke this session?',
          body: (
            <>
              {s.deviceInfo?.browser ?? 'Unknown browser'} on {s.deviceInfo?.os ?? 'unknown OS'}
              {s.location ? ` (${s.location})` : ''} is signed out on its next request.
            </>
          ),
          confirmLabel: 'Revoke session',
          danger: true,
        };
      }
      case 'revokeAll':
        return {
          title: isSelf ? 'Sign out other devices?' : 'Revoke all sessions?',
          body: `${plural(confirm.count, 'session')} will be signed out on their next request.${isSelf ? ' This device stays signed in.' : ''}`,
          confirmLabel: 'Revoke sessions',
          danger: true,
        };
      case 'revokeGrant':
        return {
          title: `Revoke ${confirm.grant.productName} access?`,
          body: `${display} loses access immediately and their refresh tokens for this app are revoked. To restore it you’ll need a new grant.`,
          confirmLabel: 'Revoke access',
          danger: true,
        };
      case 'toggleGrant':
        return confirm.on
          ? {
              title: `Resume ${confirm.grant.productName} access?`,
              body: `${display} can sign in again with roles: ${confirm.grant.roles.join(', ') || 'none'}.`,
              confirmLabel: 'Resume access',
            }
          : {
              title: `Suspend ${confirm.grant.productName} access?`,
              body: `${display} can’t sign in or refresh tokens for this app until you resume. Roles are kept.`,
              confirmLabel: 'Suspend access',
              danger: true,
            };
    }
  })();

  const invalidateGrants = () =>
    queryClient.invalidateQueries({ queryKey: ['users', user.id, 'products'] });

  const runConfirm = async () => {
    if (!confirm) return;
    try {
      switch (confirm.kind) {
        case 'disable':
        case 'enable':
          await updateUser.mutateAsync({
            id: user.id,
            data: { isActive: confirm.kind === 'enable' },
          });
          toast({
            title: confirm.kind === 'enable' ? 'Account enabled' : 'Account disabled',
            description: user.email,
          });
          queryClient.invalidateQueries({ queryKey: ['users', user.id, 'sessions'] });
          break;
        case 'forceReset': {
          const r = await forceReset.mutateAsync(user.id);
          toast({
            title: 'Reset link sent',
            description: `${user.email} · ${plural(r.sessionsRevoked, 'session')} signed out`,
          });
          break;
        }
        case 'resetPassword': {
          const temp = tempPassword();
          await updateUser.mutateAsync({ id: user.id, data: { password: temp } });
          setSecret(temp);
          break;
        }
        case 'markVerified':
          await updateUser.mutateAsync({ id: user.id, data: { emailVerified: true } });
          toast({ title: 'Email marked verified', description: user.email });
          break;
        case 'delete':
          await deleteUser.mutateAsync(user.id);
          toast({ title: 'User deleted', description: user.email });
          navigate('/users');
          break;
        case 'superAdmin':
          await updateUser.mutateAsync({ id: user.id, data: { isSuperAdmin: confirm.on } });
          toast({
            title: confirm.on ? 'Super admin granted' : 'Super admin removed',
            description: user.email,
          });
          break;
        case 'revokeSession':
          await revokeSessions.mutateAsync({ id: user.id, sessionId: confirm.session.id });
          toast({ title: 'Session revoked' });
          break;
        case 'revokeAll':
          await revokeSessions.mutateAsync({ id: user.id });
          toast({ title: 'Sessions revoked', description: plural(confirm.count, 'device') + ' signed out.' });
          break;
        case 'revokeGrant':
          await revokeAccess.mutateAsync({ productId: confirm.grant.productId, userId: user.id });
          invalidateGrants();
          toast({ title: 'Access revoked', description: confirm.grant.productName });
          break;
        case 'toggleGrant':
          await setAccessActive.mutateAsync({
            productId: confirm.grant.productId,
            userId: user.id,
            isActive: confirm.on,
          });
          invalidateGrants();
          toast({
            title: confirm.on ? 'Access resumed' : 'Access suspended',
            description: confirm.grant.productName,
          });
          break;
      }
    } catch (e) {
      toast({ variant: 'destructive', title: 'Action failed', description: errMsg(e, 'Try again.') });
    }
  };

  return (
    <div>
      <Link
        to="/users"
        className="mb-3 inline-flex items-center gap-1.5 text-[13px] text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Users
      </Link>

      {/* Head */}
      <header className="mb-6 flex flex-wrap items-start gap-4">
        <UserAvatar
          name={user.name}
          email={user.email}
          picture={user.picture}
          className="h-14 w-14 text-base"
        />
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-semibold leading-tight tracking-[-0.03em]">
            {display}
            {isSelf && <span className="ml-2 text-sm font-normal text-muted-foreground">(you)</span>}
          </h1>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[13px] text-muted-foreground">
            <span className="inline-flex min-w-0 items-center gap-1.5">
              <Mail className="h-3.5 w-3.5 shrink-0" />
              <CopyId value={user.email} />
            </span>
            <span className="inline-flex min-w-0 items-center gap-1.5">
              <span className="font-mono text-xs">id</span>
              <CopyId value={user.id} />
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              {user.lastLoginAt ? `Last sign-in ${ago(user.lastLoginAt)}` : 'Never signed in'}
            </span>
          </div>
          <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
            <StatusPill tone={user.isActive ? 'ok' : 'neutral'}>
              {user.isActive ? 'Active' : 'Disabled'}
            </StatusPill>
            <StatusPill tone={user.emailVerified ? 'ok' : 'warn'}>
              {user.emailVerified ? 'Email verified' : 'Email unverified'}
            </StatusPill>
            {user.isSuperAdmin && <Badge variant="outline">super admin</Badge>}
            <OrgTag userId={user.id} />
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setGrantDialog({})}>
            <Plus />
            Grant app access
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" aria-label="More actions">
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem disabled={user.emailVerified} onClick={handleResend}>
                <Mail className="mr-2 h-4 w-4" />
                Resend verification email
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setConfirm({ kind: 'forceReset' })}>
                <KeyRound className="mr-2 h-4 w-4" />
                Force password reset
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to={`/audit?user=${user.id}`}>
                  <ScrollText className="mr-2 h-4 w-4" />
                  View in audit log
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {user.isActive ? (
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  disabled={isSelf}
                  onClick={() => setConfirm({ kind: 'disable' })}
                >
                  <Ban className="mr-2 h-4 w-4" />
                  Disable account
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={() => setConfirm({ kind: 'enable' })}>
                  <UserCheck className="mr-2 h-4 w-4" />
                  Enable account
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <Tabs value={tab} onValueChange={changeTab}>
        <TabsList className="flex w-full max-w-full justify-start overflow-x-auto">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="access">
            App access
            <TabCount n={grants.length} />
          </TabsTrigger>
          <TabsTrigger value="sessions">
            Sessions
            <TabCount n={sessions.length} />
          </TabsTrigger>
          <TabsTrigger value="activity">
            Activity
            <TabCount n={activity?.total} />
          </TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OverviewTab
            user={user}
            isSelf={isSelf}
            onToggleSuper={(on) => setConfirm({ kind: 'superAdmin', on })}
            onResend={handleResend}
            onMarkVerified={() => setConfirm({ kind: 'markVerified' })}
            resending={resend.isPending}
          />
        </TabsContent>

        <TabsContent value="access">
          <Card className="overflow-hidden">
            <CardHead
              title="App access"
              sub="Apps this user can sign into, and the roles in their tokens."
              action={
                <Button variant="outline" size="sm" onClick={() => setGrantDialog({})}>
                  <Plus />
                  Grant access
                </Button>
              }
            />
            {grants.length === 0 ? (
              <EmptyState
                icon={ShieldCheck}
                title="No app access granted"
                body="This user can only sign into apps that are open to every signed-in user."
                action={
                  <Button variant="outline" onClick={() => setGrantDialog({})}>
                    <Plus />
                    Grant access
                  </Button>
                }
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>App</TableHead>
                    <TableHead>Roles</TableHead>
                    <TableHead>Extra permissions</TableHead>
                    <TableHead>State</TableHead>
                    <TableHead>Granted</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead className="w-10">
                      <span className="sr-only">Actions</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {grants.map((g) => {
                    const st = GRANT_PILL[grantState(g)];
                    return (
                      <TableRow
                        key={g.productId}
                        className={rowLink}
                        onClick={(e) => {
                          if (!(e.target as HTMLElement).closest('a,button,[role=menuitem]'))
                            navigate(`/products/${g.productId}`);
                        }}
                      >
                        <TableCell>
                          <Link
                            to={`/products/${g.productId}`}
                            className="flex min-w-0 items-center gap-2.5"
                          >
                            <MarkTile name={g.productName} size="sm" />
                            <span className="min-w-0">
                              <b className="block max-w-[200px] truncate font-medium">
                                {g.productName}
                              </b>
                              <span className="block max-w-[200px] truncate text-xs text-muted-foreground">
                                {g.organizationName ?? g.organizationId}
                              </span>
                            </span>
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Tags items={g.roles} empty="No roles" />
                        </TableCell>
                        <TableCell>
                          <Tags items={g.permissions} empty="—" solid />
                        </TableCell>
                        <TableCell>
                          <StatusPill tone={st.tone}>{st.label}</StatusPill>
                        </TableCell>
                        <TableCell className="whitespace-nowrap font-mono text-xs tabular-nums text-muted-foreground">
                          {fmtDate(g.grantedAt)}
                        </TableCell>
                        <TableCell className="whitespace-nowrap font-mono text-xs tabular-nums text-muted-foreground">
                          {g.expiresAt ? fmtDate(g.expiresAt) : 'Never'}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label={`Actions for ${g.productName}`}
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem asChild>
                                <Link to={`/products/${g.productId}`}>
                                  <Package className="mr-2 h-4 w-4" />
                                  Open app
                                </Link>
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setGrantDialog({ existing: g })}>
                                <Pencil className="mr-2 h-4 w-4" />
                                Edit roles &amp; expiry
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() =>
                                  setConfirm({ kind: 'toggleGrant', grant: g, on: !g.isActive })
                                }
                              >
                                {g.isActive ? (
                                  <Pause className="mr-2 h-4 w-4" />
                                ) : (
                                  <Play className="mr-2 h-4 w-4" />
                                )}
                                {g.isActive ? 'Suspend access' : 'Resume access'}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={() => setConfirm({ kind: 'revokeGrant', grant: g })}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Revoke access
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="sessions">
          <Card className="overflow-hidden">
            <CardHead
              title="Active sessions"
              sub="Devices signed in to authrz.me with this account."
              action={
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  disabled={others.length === 0}
                  onClick={() => setConfirm({ kind: 'revokeAll', count: others.length })}
                >
                  <LogOut />
                  {isSelf ? 'Sign out other devices' : 'Revoke all'}
                </Button>
              }
            />
            {sessions.length === 0 ? (
              <EmptyState
                icon={Monitor}
                title="No active sessions"
                body={`${display} isn’t signed in on any device right now.`}
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Device</TableHead>
                    <TableHead>IP address</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Last active</TableHead>
                    <TableHead>Signed in</TableHead>
                    <TableHead className="w-24">
                      <span className="sr-only">Actions</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sessions.map((s) => {
                    const dev = s.deviceInfo?.device?.toLowerCase() ?? '';
                    const Icon = dev.includes('mobile')
                      ? Smartphone
                      : dev.includes('tablet')
                        ? Tablet
                        : Laptop;
                    return (
                      <TableRow key={s.id}>
                        <TableCell>
                          <span className="flex items-center gap-2.5">
                            <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                            <span>
                              <b className="block font-medium">
                                {s.deviceInfo?.browser ?? 'Unknown browser'}
                              </b>
                              <span className="block text-xs text-muted-foreground">
                                {s.deviceInfo?.os ?? 'Unknown OS'}
                              </span>
                            </span>
                          </span>
                        </TableCell>
                        <TableCell className="font-mono text-xs tabular-nums">
                          {s.ipAddress ?? '—'}
                        </TableCell>
                        <TableCell>{s.location ?? '—'}</TableCell>
                        <TableCell className="whitespace-nowrap font-mono text-xs tabular-nums text-muted-foreground">
                          {ago(s.lastActivityAt)}
                        </TableCell>
                        <TableCell className="whitespace-nowrap font-mono text-xs tabular-nums text-muted-foreground">
                          {fmtDate(s.createdAt)}
                        </TableCell>
                        <TableCell>
                          {s.isCurrent ? (
                            <StatusPill tone="ok">This device</StatusPill>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setConfirm({ kind: 'revokeSession', session: s })}
                            >
                              Revoke
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="activity">
          <Card className="overflow-hidden">
            <CardHead
              title="Recent activity"
              sub={`Latest events where ${display} was the actor.`}
              action={
                <Button variant="outline" size="sm" asChild>
                  <Link to={`/audit?user=${user.id}`}>
                    <ScrollText />
                    Open in audit log
                  </Link>
                </Button>
              }
            />
            {!activity ? (
              <div className="p-4">
                <Skeleton className="h-24 w-full" />
              </div>
            ) : activity.data.length === 0 ? (
              <EmptyState
                icon={ScrollText}
                title="No activity yet"
                body="Sign-ins, token grants and admin changes made by this user will show here."
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Time</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Resource</TableHead>
                    <TableHead>IP address</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activity.data.map((e) => (
                    <TableRow
                      key={e.id}
                      className={rowLink}
                      onClick={() => navigate(`/audit?event=${e.id}`)}
                    >
                      <TableCell
                        className="whitespace-nowrap font-mono text-xs tabular-nums"
                        title={e.timestamp}
                      >
                        {fmtDateTime(e.timestamp)}
                      </TableCell>
                      <TableCell>
                        <Link
                          to={`/audit?event=${e.id}`}
                          className={cn(
                            'inline-flex rounded-[5px] px-1.5 py-0.5 font-mono text-[12px] font-medium',
                            /failed|denied|revoked/.test(e.action)
                              ? 'bg-destructive/[0.12] text-destructive'
                              : /rotated|disabled|deleted/.test(e.action)
                                ? 'bg-warning/[0.14] text-warning'
                                : 'bg-foreground/[0.06]'
                          )}
                        >
                          {e.action}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <span className="block text-[13px]">{e.resource}</span>
                        <span
                          className="block max-w-[220px] truncate font-mono text-xs text-muted-foreground"
                          title={e.resourceId ?? ''}
                        >
                          {e.resourceId ?? '—'}
                        </span>
                      </TableCell>
                      <TableCell className="font-mono text-xs tabular-nums text-muted-foreground">
                        {e.ipAddress ?? '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="settings">
          <SettingsTab
            key={`${user.name}|${user.email}`}
            user={user}
            isSelf={isSelf}
            onAction={(kind) => setConfirm({ kind })}
          />
        </TabsContent>
      </Tabs>

      {grantDialog && (
        <GrantDialog
          userId={user.id}
          userName={display}
          grants={grants}
          existing={grantDialog.existing}
          onClose={() => setGrantDialog(null)}
        />
      )}

      {confirm && confirmCopy && (
        <ConfirmDialog
          open
          onOpenChange={(o) => !o && setConfirm(null)}
          {...confirmCopy}
          onConfirm={runConfirm}
        />
      )}

      <SecretDialog
        open={!!secret}
        onClose={() => setSecret(null)}
        title="Temporary password set"
        description={
          <>
            This password is shown once. Share it with <span className="font-mono">{user.email}</span>{' '}
            securely; they should change it after signing in.
          </>
        }
        values={[{ label: 'Temporary password', value: secret ?? '' }]}
        ackLabel="I have copied this password"
      />
    </div>
  );
}

function TabCount({ n }: { n?: number }) {
  if (n === undefined) return null;
  return <span className="font-mono text-[11px] text-muted-foreground">{n}</span>;
}

function CardHead({
  title,
  sub,
  action,
}: {
  title: string;
  sub?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 border-b px-4 py-3.5 sm:px-[18px]">
      <div className="min-w-0">
        <h2 className="text-[15px] font-semibold leading-tight">{title}</h2>
        {sub && <p className="mt-0.5 text-[13px] text-muted-foreground">{sub}</p>}
      </div>
      {action}
    </div>
  );
}

function Tags({ items, empty, solid }: { items: string[]; empty: string; solid?: boolean }) {
  if (!items.length) return <span className="text-muted-foreground">{empty}</span>;
  return (
    <span className="flex flex-wrap gap-1">
      {items.map((r) => (
        <Badge key={r} variant={solid ? 'default' : 'outline'} className="font-mono font-normal">
          {r}
        </Badge>
      ))}
    </span>
  );
}

function OrgTag({ userId }: { userId: string }) {
  const { data: org } = useQuery({
    queryKey: ['users', userId, 'organization'],
    queryFn: () => usersApi.getOrganization(userId),
  });
  if (!org) return null;
  return (
    <Badge variant="outline">
      {org.role} · {org.name}
    </Badge>
  );
}

function KV({ children }: { children: React.ReactNode }) {
  return (
    <dl className="grid grid-cols-[110px_minmax(0,1fr)] items-center gap-x-4 gap-y-3 text-[13.5px]">
      {children}
    </dl>
  );
}

function OverviewTab({
  user,
  isSelf,
  onToggleSuper,
  onResend,
  onMarkVerified,
  resending,
}: {
  user: NonNullable<ReturnType<typeof useUser>['data']>;
  isSelf: boolean;
  onToggleSuper: (on: boolean) => void;
  onResend: () => void;
  onMarkVerified: () => void;
  resending: boolean;
}) {
  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
      <div className="space-y-4">
        <Card className="overflow-hidden">
          <CardHead title="Profile" sub="Identity details issued in this user’s ID tokens." />
          <div className="p-4 sm:p-[18px]">
            <KV>
              <dt className="text-muted-foreground">Name</dt>
              <dd>{user.name || <span className="text-muted-foreground">—</span>}</dd>
              <dt className="text-muted-foreground">Email</dt>
              <dd className="flex flex-wrap items-center gap-2">
                <span className="break-all font-mono text-[13px]">{user.email}</span>
                <StatusPill tone={user.emailVerified ? 'ok' : 'warn'}>
                  {user.emailVerified ? 'Verified' : 'Unverified'}
                </StatusPill>
              </dd>
              <dt className="text-muted-foreground">User ID</dt>
              <dd className="min-w-0">
                <CopyId value={user.id} />
              </dd>
              <dt className="text-muted-foreground">Status</dt>
              <dd>
                <StatusPill tone={user.isActive ? 'ok' : 'neutral'}>
                  {user.isActive ? 'Active' : 'Disabled'}
                </StatusPill>
              </dd>
              <dt className="text-muted-foreground">Created</dt>
              <dd className="font-mono text-xs tabular-nums">{fmtDateTime(user.createdAt)}</dd>
              <dt className="text-muted-foreground">Last sign-in</dt>
              <dd className="font-mono text-xs tabular-nums">
                {user.lastLoginAt ? fmtDateTime(user.lastLoginAt) : 'Never'}
              </dd>
            </KV>
          </div>
        </Card>
        <OrganizationCard userId={user.id} />
      </div>

      <div className="space-y-4">
        <Card className="overflow-hidden">
          <CardHead title="Platform role" />
          <div className="flex items-center justify-between gap-4 p-4 sm:p-[18px]">
            <div>
              <Label htmlFor="ud-super" className="text-[13.5px] font-semibold">
                Super admin
              </Label>
              <p className="mt-0.5 text-[13px] text-muted-foreground">
                {isSelf
                  ? 'You can’t change your own super admin role.'
                  : 'Manage every organization, app, client, signing key and the audit log.'}
              </p>
            </div>
            <Switch
              id="ud-super"
              checked={user.isSuperAdmin}
              disabled={isSelf}
              onCheckedChange={(on) => onToggleSuper(on)}
            />
          </div>
        </Card>

        <Card className="overflow-hidden">
          <CardHead title="Email" />
          <div className="p-4 sm:p-[18px]">
            {user.emailVerified ? (
              <Notice tone="info" title="Verified">
                <span className="font-mono">{user.email}</span> is confirmed.
              </Notice>
            ) : (
              <>
                <Notice tone="warn" title="Not verified">
                  They can’t sign in to gated apps until they confirm{' '}
                  <span className="font-mono">{user.email}</span>.
                </Notice>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={onResend} disabled={resending}>
                    {resending ? <Loader2 className="animate-spin" /> : <Mail />}
                    Resend email
                  </Button>
                  <Button variant="ghost" size="sm" onClick={onMarkVerified}>
                    <CheckCircle2 />
                    Mark as verified
                  </Button>
                </div>
              </>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

function OrganizationCard({ userId }: { userId: string }) {
  const { toast } = useToast();
  const { data: organizations = [] } = useOrganizations();
  const setOrg = useSetUserOrganization();
  const removeOrg = useRemoveUserOrganization();
  const queryClient = useQueryClient();

  const { data: current, isLoading } = useQuery({
    queryKey: ['users', userId, 'organization'],
    queryFn: () => usersApi.getOrganization(userId),
  });
  const { data: roster = [] } = useOrganizationMembers(current?.id);
  const soleOwner =
    !!current && current.role === 'owner' && roster.filter((m) => m.role === 'owner').length <= 1;

  const [orgId, setOrgId] = useState('');
  const [role, setRole] = useState<Role>('member');
  const [removing, setRemoving] = useState(false);

  const fail = (title: string, e: unknown) =>
    toast({ variant: 'destructive', title, description: errMsg(e, 'Try again.') });

  const changeRole = async (next: Role) => {
    if (!current || next === current.role) return;
    try {
      await setOrg.mutateAsync({ id: userId, organizationId: current.id, role: next });
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      toast({ title: 'Role updated', description: `Now ${next} of ${current.name}.` });
    } catch (e) {
      fail('Could not update role', e);
    }
  };

  const handleAdd = async () => {
    if (!orgId) return;
    try {
      await setOrg.mutateAsync({ id: userId, organizationId: orgId, role });
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      toast({ title: current ? 'Moved to organization' : 'Added to organization' });
      setOrgId('');
    } catch (e) {
      fail('Could not set organization', e);
    }
  };

  return (
    <Card className="overflow-hidden">
      <CardHead title="Organization" sub="Users belong to at most one organization." />
      <div className="space-y-4 p-4 sm:p-[18px]">
        {isLoading ? (
          <Skeleton className="h-10 w-full" />
        ) : current ? (
          <>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <Link
                to={`/organizations/${current.id}`}
                className="flex min-w-0 flex-1 items-center gap-2.5"
              >
                <MarkTile name={current.name} />
                <span className="min-w-0">
                  <b className="block truncate font-medium">{current.name}</b>
                  <span className="block truncate font-mono text-xs text-muted-foreground">
                    {current.slug}
                    {roster.length > 0 && ` · ${plural(roster.length, 'member')}`}
                  </span>
                </span>
              </Link>
              <Select
                value={current.role}
                onValueChange={(v) => changeRole(v as Role)}
                disabled={soleOwner || setOrg.isPending}
              >
                <SelectTrigger aria-label="Organization role" className="w-full sm:w-[130px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ORG_ROLES.map((r) => (
                    <SelectItem key={r} value={r}>
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive"
                disabled={soleOwner}
                onClick={() => setRemoving(true)}
              >
                Remove
              </Button>
            </div>
            {soleOwner && (
              <p className="text-xs text-muted-foreground">
                Only owner of this organization. Make someone else owner before changing this role
                or removing them.
              </p>
            )}
          </>
        ) : (
          <p className="text-[13.5px] text-muted-foreground">Not a member of any organization.</p>
        )}

        <div className={cn('flex flex-col gap-3 sm:flex-row sm:items-end', current && 'border-t pt-4')}>
          <div className="flex-1 space-y-2">
            <Label>{current ? 'Move to organization' : 'Add to organization'}</Label>
            <Select value={orgId} onValueChange={setOrgId}>
              <SelectTrigger>
                <SelectValue placeholder="Select organization" />
              </SelectTrigger>
              <SelectContent>
                {organizations
                  .filter((o) => o.id !== current?.id)
                  .map((o) => (
                    <SelectItem key={o.id} value={o.id}>
                      {o.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Role</Label>
            <Select value={role} onValueChange={(v) => setRole(v as Role)}>
              <SelectTrigger className="w-full sm:w-[130px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ORG_ROLES.map((r) => (
                  <SelectItem key={r} value={r}>
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={handleAdd} disabled={!orgId || setOrg.isPending}>
            <Plus />
            {current ? 'Move' : 'Add to organization'}
          </Button>
        </div>
      </div>

      {current && (
        <ConfirmDialog
          open={removing}
          onOpenChange={setRemoving}
          title={`Remove from ${current.name}?`}
          body="They lose access granted through organization membership. Direct app grants are kept."
          confirmLabel="Remove member"
          danger
          onConfirm={async () => {
            try {
              await removeOrg.mutateAsync(userId);
              queryClient.invalidateQueries({ queryKey: ['organizations'] });
              toast({ title: 'Removed from organization', description: current.name });
            } catch (e) {
              fail('Could not remove', e);
            }
          }}
        />
      )}
    </Card>
  );
}

function SettingsTab({
  user,
  isSelf,
  onAction,
}: {
  user: NonNullable<ReturnType<typeof useUser>['data']>;
  isSelf: boolean;
  onAction: (
    kind: 'disable' | 'enable' | 'delete' | 'forceReset' | 'resetPassword'
  ) => void;
}) {
  const { toast } = useToast();
  const updateUser = useUpdateUser();
  const [name, setName] = useState(user.name ?? '');
  const [email, setEmail] = useState(user.email);
  const [touched, setTouched] = useState(false);

  const nameErr = name.trim() ? '' : 'Enter a name.';
  const emailErr = !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
    ? 'Enter a valid email, like name@company.com.'
    : '';
  const dirty = name.trim() !== (user.name ?? '') || email.trim() !== user.email;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (nameErr || emailErr || !dirty) {
      if (!dirty) toast({ title: 'No changes to save' });
      return;
    }
    const emailChanged = email.trim() !== user.email;
    try {
      await updateUser.mutateAsync({
        id: user.id,
        data: {
          name: name.trim(),
          email: email.trim(),
          ...(emailChanged ? { emailVerified: false } : {}),
        },
      });
      toast({
        title: 'Account updated',
        description: emailChanged ? 'The new email is unverified until confirmed.' : undefined,
      });
      setTouched(false);
    } catch (err) {
      toast({ variant: 'destructive', title: 'Could not save', description: errMsg(err, 'Try again.') });
    }
  };

  return (
    <div className="max-w-[760px] space-y-4">
      <Card className="overflow-hidden">
        <form onSubmit={submit} noValidate>
          <CardHead
            title="Account details"
            sub="Changing the email marks it unverified until they confirm the new address."
          />
          <div className="grid gap-4 p-4 sm:grid-cols-2 sm:p-[18px]">
            <div className="space-y-2">
              <Label htmlFor="ud-name">Full name</Label>
              <Input
                id="ud-name"
                autoComplete="off"
                value={name}
                aria-invalid={touched && !!nameErr}
                onChange={(e) => setName(e.target.value)}
              />
              {touched && nameErr && <p className="text-xs text-destructive">{nameErr}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="ud-email">Email</Label>
              <Input
                id="ud-email"
                type="email"
                autoComplete="off"
                className="font-mono"
                value={email}
                aria-invalid={touched && !!emailErr}
                onChange={(e) => setEmail(e.target.value)}
              />
              {touched && emailErr && <p className="text-xs text-destructive">{emailErr}</p>}
            </div>
          </div>
          <div className="flex justify-end gap-2 border-t px-4 py-3 sm:px-[18px]">
            <Button
              type="button"
              variant="ghost"
              disabled={!dirty}
              onClick={() => {
                setName(user.name ?? '');
                setEmail(user.email);
                setTouched(false);
              }}
            >
              Reset
            </Button>
            <Button type="submit" disabled={updateUser.isPending || !dirty}>
              {updateUser.isPending && <Loader2 className="animate-spin" />}
              Save changes
            </Button>
          </div>
        </form>
      </Card>

      <Card className="overflow-hidden border-destructive/40">
        <CardHead
          title="Danger zone"
          sub="These actions take effect immediately and are recorded in the audit log."
        />
        <div className="divide-y">
          <SettingRow
            title="Force password reset"
            body={
              <>
                Signs them out everywhere and emails a reset link to{' '}
                <span className="font-mono">{user.email}</span>.
              </>
            }
          >
            <Button variant="outline" size="sm" onClick={() => onAction('forceReset')}>
              Send reset link
            </Button>
          </SettingRow>
          <SettingRow
            title="Set temporary password"
            body="Replaces their password with a generated one that you share with them, shown once."
          >
            <Button variant="outline" size="sm" onClick={() => onAction('resetPassword')}>
              <KeyRound />
              Set password
            </Button>
          </SettingRow>
          <SettingRow
            title={user.isActive ? 'Disable account' : 'Enable account'}
            body={
              user.isActive
                ? 'Blocks sign-in and ends every session. Grants and organization role are kept.'
                : 'Lets them sign in again with their existing grants.'
            }
          >
            {user.isActive ? (
              <Button
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive"
                disabled={isSelf}
                title={isSelf ? 'You can’t disable yourself' : undefined}
                onClick={() => onAction('disable')}
              >
                Disable account
              </Button>
            ) : (
              <Button variant="outline" size="sm" onClick={() => onAction('enable')}>
                Enable account
              </Button>
            )}
          </SettingRow>
          <SettingRow
            title="Delete user"
            body={
              isSelf
                ? 'You can’t delete your own account here.'
                : 'Permanently removes the account, its grants and its organization membership.'
            }
          >
            <Button
              variant="outline"
              size="sm"
              className="text-destructive hover:text-destructive"
              disabled={isSelf}
              onClick={() => onAction('delete')}
            >
              <Trash2 />
              Delete user
            </Button>
          </SettingRow>
        </div>
      </Card>
    </div>
  );
}

function SettingRow({
  title,
  body,
  children,
}: {
  title: string;
  body: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3 px-4 py-3.5 sm:flex-row sm:items-center sm:justify-between sm:px-[18px]">
      <div className="min-w-0">
        <b className="text-[13.5px] font-semibold">{title}</b>
        <p className="mt-0.5 text-[13px] text-muted-foreground">{body}</p>
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

function GrantDialog({
  userId,
  userName,
  grants,
  existing,
  onClose,
}: {
  userId: string;
  userName: string;
  grants: UserAppAccess[];
  existing?: UserAppAccess;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: allProducts = [] } = useProducts();
  const grant = useGrantProductAccess();
  const updateAccess = useUpdateProductAccess();
  const createRole = useCreateProductRole();

  const taken = new Set(grants.map((g) => g.productId));
  const options = existing
    ? allProducts.filter((p) => p.id === existing.productId)
    : allProducts.filter((p) => !taken.has(p.id));

  const [productId, setProductId] = useState(existing?.productId ?? '');
  const [roles, setRoles] = useState<string[]>(existing?.roles ?? []);
  const [expiry, setExpiry] = useState<string>(existing ? 'keep' : 'never');
  const [defining, setDefining] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newPermissions, setNewPermissions] = useState('');

  const { data: catalog = [] } = useProductRoles(productId || undefined);
  const known = new Set(catalog.map((r) => r.name));
  const roleRows = [
    ...catalog,
    ...roles.filter((r) => !known.has(r)).map((name) => ({ name, description: null, permissions: [] })),
  ];

  const toggle = (name: string, on: boolean) =>
    setRoles((cur) => (on ? [...new Set([...cur, name])] : cur.filter((r) => r !== name)));

  const parseCsv = (v: string) =>
    v
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean);

  const defineRole = async () => {
    const name = newName.trim();
    if (!productId || !name) return;
    try {
      await createRole.mutateAsync({
        productId,
        data: {
          name,
          description: newDescription.trim() || undefined,
          permissions: parseCsv(newPermissions),
        },
      });
      toggle(name, true);
      setNewName('');
      setNewDescription('');
      setNewPermissions('');
      setDefining(false);
      toast({ title: `Role “${name}” defined`, description: 'It is selected for this grant.' });
    } catch (e) {
      toast({ variant: 'destructive', title: 'Could not define role', description: errMsg(e, 'Try again.') });
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!productId) return;
    const product = allProducts.find((p) => p.id === productId);
    const expiresAt =
      expiry === 'keep'
        ? (existing?.expiresAt ?? null)
        : expiry === 'never'
          ? null
          : new Date(Date.now() + Number(expiry) * 864e5).toISOString();
    try {
      if (existing) {
        await updateAccess.mutateAsync({
          productId,
          userId,
          data: { roles, permissions: existing.permissions, expires_at: expiresAt },
        });
        toast({ title: 'Access updated', description: `${product?.name} · ${roles.join(', ') || 'no roles'}` });
      } else {
        await grant.mutateAsync({
          productId,
          data: { user_id: userId, roles, ...(expiresAt ? { expires_at: expiresAt } : {}) },
        });
        toast({ title: 'Access granted', description: `${userName} can now sign into ${product?.name}.` });
      }
      queryClient.invalidateQueries({ queryKey: ['users', userId, 'products'] });
      onClose();
    } catch (err) {
      toast({ variant: 'destructive', title: 'Could not save access', description: errMsg(err, 'Try again.') });
    }
  };

  const pending = grant.isPending || updateAccess.isPending;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>{existing ? 'Edit access' : 'Grant app access'}</DialogTitle>
            <DialogDescription>
              {existing
                ? `Change ${userName}’s roles and expiry.`
                : `Let ${userName} sign into an app with specific roles.`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label>App</Label>
            <Select
              value={productId}
              onValueChange={(v) => {
                setProductId(v);
                setRoles([]);
              }}
              disabled={!!existing}
            >
              <SelectTrigger>
                <SelectValue placeholder={options.length ? 'Select app' : 'Every app already granted'} />
              </SelectTrigger>
              <SelectContent>
                {options.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                    {p.organization_name ? ` — ${p.organization_name}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label id="grant-roles-label">Roles</Label>
            {!productId ? (
              <p className="text-xs text-muted-foreground">Select an app to see its roles.</p>
            ) : roleRows.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                This app has no roles defined. The grant lets them sign in with no role claims.
              </p>
            ) : (
              <div
                role="group"
                aria-labelledby="grant-roles-label"
                className="max-h-48 space-y-1 overflow-y-auto rounded-md border p-1.5"
              >
                {roleRows.map((r) => (
                  <label
                    key={r.name}
                    className="flex cursor-pointer items-start gap-2.5 rounded-md px-2 py-1.5 hover:bg-foreground/[0.04]"
                  >
                    <Checkbox
                      className="mt-0.5"
                      checked={roles.includes(r.name)}
                      onCheckedChange={(c) => toggle(r.name, c === true)}
                    />
                    <span className="min-w-0 text-[13px]">
                      <b className="font-mono font-medium">{r.name}</b>
                      <small className="block text-xs text-muted-foreground">
                        {r.description || 'No description'}
                        {r.permissions.length > 0 && ` · ${r.permissions.join(', ')}`}
                      </small>
                    </span>
                  </label>
                ))}
              </div>
            )}
            {productId && !defining && (
              <Button type="button" variant="ghost" size="sm" onClick={() => setDefining(true)}>
                <Plus />
                Define a new role
              </Button>
            )}
            {productId && defining && (
              <div className="space-y-2 rounded-md border bg-foreground/[0.02] p-3">
                <div className="grid gap-2 sm:grid-cols-2">
                  <Input
                    aria-label="Role name"
                    className="font-mono"
                    placeholder="editor"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                  />
                  <Input
                    aria-label="Description"
                    placeholder="Can edit content"
                    value={newDescription}
                    onChange={(e) => setNewDescription(e.target.value)}
                  />
                </div>
                <Input
                  aria-label="Default permissions"
                  className="font-mono"
                  placeholder="content:read, content:write"
                  value={newPermissions}
                  onChange={(e) => setNewPermissions(e.target.value)}
                />
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="ghost" size="sm" onClick={() => setDefining(false)}>
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!newName.trim() || createRole.isPending}
                    onClick={defineRole}
                  >
                    Define &amp; select
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>Expires</Label>
            <Select value={expiry} onValueChange={setExpiry}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {existing && <SelectItem value="keep">Keep current expiry</SelectItem>}
                <SelectItem value="never">Never</SelectItem>
                <SelectItem value="7">In 7 days</SelectItem>
                <SelectItem value="30">In 30 days</SelectItem>
                <SelectItem value="90">In 90 days</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Expired grants stop issuing tokens for this app.
            </p>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!productId || pending}>
              {pending && <Loader2 className="animate-spin" />}
              {existing ? 'Save changes' : 'Grant access'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

